import { ClientSideRowModelStep, GridApi, IRowNode, RowNode } from 'ag-grid-community';
import { isNil } from 'lodash-es';

export class NodeUtils {
  private static readonly defaultGetChildrenStep: ClientSideRowModelStep = 'sort';

  public static hasChildren<T>(node: IRowNode<T> | null | undefined) {
    if (node instanceof RowNode) {
      return node.hasChildren();
    }

    return !isNil(node) && node.group;
  }

  /*
    Why do we have a step parameter ?

    in normal scenarios we'll get the children after all client model steps are completed which is the sorted children.
    But if we need the children in a filter, we'll be in the filter step of the client model steps and sorted children
    are a couple steps later so if you call getChildren in a filter, you'll receive an empty list of children.

    reference: https://www.ag-grid.com/javascript-data-grid/client-side-row-stages/
  */
  public static getChildren<T>(node: IRowNode<T> | null | undefined, step = this.defaultGetChildrenStep) {
    if (step === 'sort' || step === 'map') {
      return node?.childrenAfterSort ?? [];
    }

    if (step === 'filter' || step === 'aggregate') {
      return node?.childrenAfterFilter ?? [];
    }

    if (step === 'everything') {
      return node?.childrenAfterGroup ?? [];
    }

    throw new Error('Feature not implemented');
  }

  public static scrollToNode<T>(node: IRowNode<T> | undefined, gridApi: GridApi | null | undefined) {
    if (!node) return;

    let rowIndex = 0;
    let current: IRowNode<T> | null = node;
    while (current.level >= 0) {
      rowIndex += current.childIndex ?? 0;
      current = current.parent;

      if (!current) break;
    }

    gridApi?.ensureIndexVisible(rowIndex);
  }

  public static expandNodeStructure<T>(node: IRowNode<T>, expand = true, step = this.defaultGetChildrenStep) {
    if (!node.group) return;

    // expand the node until all of its children
    const children = this.getChildren(node, step);
    if (node.expanded != expand) {
      node.setExpanded(expand);
      children.forEach(c => this.expandNodeStructure(c, expand, step));
    }
  }

  public static getRootNode<T>(node: IRowNode<T>) {
    let current = node;

    while (current.level > 0 && current.parent) {
      current = current.parent;
    }

    return current;
  }

  public static getTreePath<T>(node: IRowNode<T>) {
    let current = node;
    const treePath: string[] = [];

    while (current.level >= 0 && current.parent) {
      treePath.push(current.key ?? 'undefined');
      current = current.parent;
    }

    return treePath.reverse();
  }
}
